import React from "react";
import { Box, Typography, Paper, Container, useTheme } from "@mui/material";
import { styled } from "@mui/system";
import ServicesList from "../components/dynamic/ServicesList.tsx";
import WavyDivider from "../components/common/WavyDivider.tsx";

const HeaderSection = styled(Box)(({ theme }) => ({
    background: `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.background.paper})`,
    padding: "40px 20px",
    textAlign: "center",
    borderBottomLeftRadius: "30px",
    borderBottomRightRadius: "30px",
    color: theme.palette.text.primary,
}));

const washers = [
    { size: "30-lb Washer", loads: 3 },
    { size: "40-lb Washer", loads: 4 },
    { size: "60-lb Washer", loads: 6 },
    { size: "90-lb Washer", loads: 9 },
];

interface WasherCircleProps {
    loads: number;
}

const WasherCircle = styled(Paper, {
    shouldForwardProp: (prop) => prop !== "loads", // Prevents React warnings
})<WasherCircleProps>(({ theme, loads }) => ({
    width: 140 + loads * 10, // Circles scale with washer size
    height: 140 + loads * 10,
    borderRadius: "50%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
    border: `6px solid ${theme.palette.text.primary}`, // Uses primary color for border
    backgroundColor: theme.palette.background.paper, // Matches theme's background
    color: theme.palette.text.primary,
    position: "relative",
    overflow: "hidden",
    "&::after": {
        content: '""',
        position: "absolute",
        width: "100%",
        height: "100%",
        borderRadius: "50%",
        border: `6px solid ${theme.palette.primary.main}`,
        clipPath: `inset(${100 - loads * 10}% 0 0 0)`, // Adjusts outer arc dynamically
    },
}));

const AllServices = () => {
    const theme = useTheme(); // Get theme colors
    return (
        <Box sx={{ backgroundColor: theme.palette.background.default }}>
            {/* 🔥 Updated Header Section */}
            <HeaderSection>
                <Typography variant="h4" fontWeight="bold">
                    Premium Laundromat Services
                </Typography>
                <Typography variant="subtitle1">
                    Fast, Reliable, and Convenient Washing Solutions for Everyone!
                </Typography>
            </HeaderSection>

            <Container sx={{ py: 6, display: "flex", flexDirection: "column", alignItems: "center" }}>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: { xs: "column", md: "row" },
                        alignItems: "center",
                        gap: 4,
                        maxWidth: "900px",
                        textAlign: "left",
                    }}
                >
                    {/* Text Section */}
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="body1" color="text.primary">
                            We are a card-operated laundromat with NEW machines of several sizes, including 30, 40, 60, and JUMBO 90-pound machines. Our machines accept credit, debit, Apple Pay, Android Pay, and cash.
                            We also offer Pick Up and Delivery and Wash and Fold with Same-Day pick up.
                        </Typography>
                    </Box>

                    {/* Image */}
                    <Box sx={{ flex: 1, textAlign: "center" }}>
                        <img
                            src="/SelfService.png"
                            alt="Laundromat Machines"
                            style={{
                                width: "100%",
                                maxWidth: "400px",
                                borderRadius: "12px",
                                boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
                            }}
                        />
                    </Box>
                </Box>
            </Container>

            <WavyDivider />

            {/* 🌀 Washer Size Section */}
            <Box sx={{ textAlign: "center", py: 6 }}>
                <Typography variant="h4" fontWeight="bold" mb={4} color="text.primary">
                    How Large Are Our Machines?
                </Typography>

                {/* Washer Display */}
                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        justifyContent: "center",
                        gap: 4,
                    }}
                >
                    {washers.map((washer, index) => (
                        <WasherCircle key={index} loads={washer.loads}>
                            <Typography variant="h6" fontWeight="bold">
                                {washer.size}
                            </Typography>
                            <Typography variant="body2">
                                {washer.loads} LOADS
                            </Typography>
                        </WasherCircle>
                    ))}
                </Box>
            </Box>

            <WavyDivider />

            {/* 🔹 Services List Section */}
            <ServicesList />
        </Box>
    );
};

export default AllServices;
